/**
 * Componente CorporateTitle
 * Título reutilizable con estilos corporativos consistentes
 * Aplica automáticamente gradientes o colores según la variante
 */

import React from 'react';
import { Typography, TypographyProps, useTheme } from '@mui/material';
import { createGradientTitleStyles } from '../../theme/utils';

export type TitleVariant = 'gradient' | 'primary' | 'accent';

interface CorporateTitleProps extends Omit<TypographyProps, 'variant'> {
  /**
   * Variante del título
   * - 'gradient': Aplica gradiente corporativo (para títulos principales)
   * - 'primary': Usa text.primary (para títulos de sección)
   * - 'accent': Usa secondary.main/accent (para títulos destacados)
   */
  variant?: TitleVariant;
  
  /**
   * Nivel del título (h1-h6)
   */
  level?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  
  /**
   * Contenido del título
   */
  children: React.ReactNode;
}

/**
 * Obtiene los estilos según la variante del título
 */
const getTitleStyles = (
  variant: TitleVariant,
  theme: any
): React.CSSProperties => {
  switch (variant) {
    case 'gradient':
      return createGradientTitleStyles(theme);
    case 'primary':
      return {
        color: theme.palette.text.primary,
        fontWeight: 600,
      };
    case 'accent':
      return {
        color: theme.custom.colors.accent.main,
        fontWeight: 600,
      };
    default:
      return {
        color: theme.palette.text.primary,
        fontWeight: 600,
      };
  }
};

/**
 * Mapea el nivel a la variante de Typography de MUI
 */
const getTypographyVariant = (level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => {
  return level;
};

export const CorporateTitle: React.FC<CorporateTitleProps> = ({
  variant = 'gradient',
  level = 'h4',
  children,
  sx,
  ...props
}) => {
  const theme = useTheme();
  const styles = getTitleStyles(variant, theme);
  
  return (
    <Typography
      variant={getTypographyVariant(level)}
      sx={{
        ...styles,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};

